import type { FC, PropsWithChildren } from 'react';

import { Icon } from '@iconify/react';
import { Alert, AlertTitle, CircularProgress } from '@mui/material';
import { tss } from 'tss-react/mui';

type Props = {
  error?: Error | null | string;
  items?: {
    error?: Error;
    loading: boolean;
    text: string;
  }[];
  loading?: boolean;
  text?: string;
};

const useStyles = tss.create(({ theme }) => ({
  checkmark: {
    color: theme.palette.success.main,
    fontSize: 12,
  },
  errorList: {
    margin: 0,
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(2),
  },
  icon: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  root: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
}));

export const LoadingWrapper: FC<PropsWithChildren<Props>> = ({
  children,
  error,
  items,
  loading,
  text,
}) => {
  const { classes } = useStyles();

  if (error) {
    return (
      <Alert severity="error">
        {typeof error === 'string' ? error : error.message}
      </Alert>
    );
  }

  const isLoading = loading ?? (items?.some(item => item.loading) ?? false);

  const errors = items?.filter(item => item.error);
  if (errors?.length) {
    return (
      <Alert severity="error">
        <AlertTitle>There are errors on the requested page</AlertTitle>

        <ul className={classes.errorList}>
          {errors.map(item => (
            <li key={item.text}>
              {typeof item.error === 'string'
                ? item.error
                : item.error?.message}
            </li>
          ))}
        </ul>
      </Alert>
    );
  }

  if (!isLoading) {
    return children;
  }

  const loadingItem = items?.find(item => item.loading);
  return (
    <div className={classes.root}>
      <CircularProgress />

      {items
        ? (
            <>
              {items
                .filter(item => !item.loading)
                .map(item => (
                  <div className={classes.icon} key={item.text}>
                    <Icon
                      className={classes.checkmark}
                      icon="material-symbols:check"
                    />
                    <span>{item.text}</span>
                  </div>
                ))}
              {loadingItem && (
                <div className={classes.icon}>
                  <CircularProgress size={12} />
                  <span>{loadingItem.text}</span>
                </div>
              )}
            </>
          )
        : (
            <span>{text}</span>
          )}
    </div>
  );
};
